import React from "react"
import * as SelectPrimitive from "@radix-ui/react-select"
import { Check, ChevronDown, ChevronUp } from "lucide-react"
import { cn } from "@/lib/utils"

/**
 * Select - Komponent wyboru opcji w stylu Radix Themes
 * 
 * API zgodne z @radix-ui/themes
 * - Rozmiary: "1" | "2" | "3"
 * - Warianty: "classic" | "surface" | "soft" | "ghost"
 * - Accessibility (a11y) - keyboard navigation, aria
 * 
 * @example
 * ```tsx
 * <Select>
 *   <SelectTrigger variant="surface" size="2">
 *     <SelectValue placeholder="Wybierz opcję" />
 *   </SelectTrigger>
 *   <SelectContent>
 *     <SelectItem value="option1">Opcja 1</SelectItem>
 *     <SelectItem value="option2">Opcja 2</SelectItem>
 *   </SelectContent>
 * </Select>
 * ```
 */

const Select = SelectPrimitive.Root
const SelectGroup = SelectPrimitive.Group
const SelectValue = SelectPrimitive.Value

/**
 * SelectTrigger - Przycisk otwierający select
 */
const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
    variant?: "classic" | "surface" | "soft" | "ghost"
    size?: "1" | "2" | "3"
  }
>(({ className, variant = "surface", size = "2", children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      "flex w-full items-center justify-between",
      "transition-all duration-150",
      "focus:outline-none focus:ring-3 focus:ring-[var(--pink-9)] focus:ring-offset-2", // WCAG AAA enhanced focus
      "disabled:cursor-not-allowed disabled:opacity-50",
      "[&>span]:line-clamp-1",
      // Rozmiary
      size === "1" && cn(
        "h-[24px]",
        "rounded-[var(--radius-sm)]",
        "px-[var(--space-3)] py-[var(--space-1)]",
        "text-[var(--font-size-1)]"
      ),
      size === "2" && cn(
        "h-[32px]",
        "rounded-[var(--radius-base)]",
        "px-[var(--space-4)] py-[var(--space-2)]",
        "text-[var(--font-size-2)]"
      ),
      size === "3" && cn(
        "h-[40px]",
        "rounded-[var(--radius-md)]",
        "px-[var(--space-5)] py-[var(--space-3)]",
        "text-[var(--font-size-3)]"
      ),
      // Warianty
      variant === "classic" && cn(
        "bg-[var(--dark-2)]",
        "border border-[var(--dark-a7)]",
        "text-[var(--text-white-95)]",
        "hover:border-[var(--pink-a6)]",
        "shadow-[0_1px_2px_0_rgba(0,0,0,0.1)]"
      ),
      variant === "surface" && cn(
        "bg-[var(--dark-2)]",
        "border border-[var(--dark-a5)]",
        "text-[var(--text-white-95)]",
        "hover:border-[var(--pink-a4)]",
        "shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]"
      ),
      variant === "soft" && cn(
        "bg-[var(--dark-a2)]",
        "border border-transparent",
        "text-[var(--text-white-95)]",
        "hover:bg-[var(--dark-a3)]"
      ),
      variant === "ghost" && cn(
        "bg-transparent",
        "border border-transparent",
        "text-[var(--text-white-95)]",
        "hover:bg-[var(--dark-a2)]"
      ),
      className
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="h-4 w-4 opacity-50" aria-hidden="true" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
))
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

/**
 * SelectScrollUpButton - Scroll do góry
 */
const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className
    )}
    aria-label="Przewiń w górę"
    {...props}
  >
    <ChevronUp className="h-4 w-4" aria-hidden="true" />
  </SelectPrimitive.ScrollUpButton>
))
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName

/**
 * SelectScrollDownButton - Scroll w dół
 */
const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className
    )}
    aria-label="Przewiń w dół"
    {...props}
  >
    <ChevronDown className="h-4 w-4" aria-hidden="true" />
  </SelectPrimitive.ScrollDownButton>
))
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName

/**
 * SelectContent - Lista opcji (dropdown)
 */
const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = "popper", ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        "relative z-50",
        "max-h-[400px] min-w-[8rem]",
        "overflow-hidden",
        "rounded-[var(--radius-md)]",
        "bg-[var(--dark-2)]",
        "border border-[var(--dark-a6)]",
        "text-[var(--text-white-95)]",
        "shadow-[0_8px_16px_0_rgba(0,0,0,0.3)]",
        "data-[state=open]:animate-in data-[state=closed]:animate-out",
        "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
        "data-[side=bottom]:slide-in-from-top-2",
        "data-[side=left]:slide-in-from-right-2",
        "data-[side=right]:slide-in-from-left-2",
        "data-[side=top]:slide-in-from-bottom-2",
        position === "popper" &&
          "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
        className
      )}
      position={position}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          "p-[var(--space-2)]",
          position === "popper" &&
            "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]"
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
))
SelectContent.displayName = SelectPrimitive.Content.displayName

/**
 * SelectLabel - Etykieta grupy opcji
 */
const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn(
      "px-[var(--space-4)] py-[var(--space-3)]",
      "text-[var(--font-size-1)]",
      "font-[var(--font-weight-semibold)]",
      "text-[var(--text-white-80)]",
      "uppercase tracking-wide",
      className
    )}
    {...props}
  />
))
SelectLabel.displayName = SelectPrimitive.Label.displayName

/**
 * SelectItem - Pojedyncza opcja
 */
const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex w-full cursor-pointer select-none items-center",
      "rounded-[var(--radius-sm)]",
      "py-[var(--space-3)] pl-[var(--space-8)] pr-[var(--space-4)]",
      "text-[var(--font-size-2)]",
      "outline-none",
      "transition-colors",
      "focus:bg-[var(--dark-3)] focus:text-[var(--pink-11)]",
      "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    )}
    {...props}
  >
    <span className="absolute left-[var(--space-2)] flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="h-4 w-4 text-[var(--pink-9)]" aria-hidden="true" />
        <span className="sr-only">Zaznaczone</span>
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
))
SelectItem.displayName = SelectPrimitive.Item.displayName

/**
 * SelectSeparator - Separator między opcjami
 */
const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn(
      "-mx-1 my-1 h-px",
      "bg-[var(--dark-a6)]",
      className
    )}
    {...props}
  />
))
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
}

